// @ts-strict-ignore
import type { WorkflowState } from 'flow/store/redux/state';
import type { SnippetVersion } from '../types';
import type { EquivalencyKey, WorkflowPrimitive } from './equivalency';
import { getEquivalencyKeyForPrimitive } from './equivalency';
import {
  isAction, isOutcome, isPublishedAttribute, isReasonCodeGroup, isTag,
} from './equivalency/guards';
import type { EquivalencyContext } from './equivalency/types';
import type {
  Outcome, PublishedAttribute, ReasonCodeGroup, Tag,
} from 'flow/store/engine/types';


/**
 * Returns a Map mapping all equivalency keys in the snippet to colliding equivalency keys
 * in the workflow
 * @unused - no longer used, but we might bring it back later
 */
export function getCollisionMapForSnippetInsertion(args: {
  snippet: SnippetVersion;
  workflow: {
    workflowData: WorkflowState['workflowData'];
    // todo - use wf props once we understand ephemeral artifacts
  };
}) {
  const {
    snippet,
    workflow: { workflowData },
  } = args;

  const snippetPrimitives = buildPrimitives({
    actions: snippet.data.actions,
  });

  const workflowPrimitives = buildPrimitives({
    actions: workflowData.actions,
  });

  const workflowCollisionMap = getCollisionsWithinPrimitives({
    primitives: workflowPrimitives,
  });

  const snippetCollisionMap = getCollisionsWithinPrimitives({
    primitives: snippetPrimitives,
  });

  return intersectCollisionMaps({
    collisionMapA: snippetCollisionMap,
    collisionMapB: workflowCollisionMap,
  });
}


/**
 * Finds the common elements between two collision maps and returns them in a new Map
 */
function intersectCollisionMaps(args: {
  collisionMapA: Map<EquivalencyKey, Set<string>>;
  collisionMapB: Map<EquivalencyKey, Set<string>>;
}): Map<EquivalencyKey, Set<string>> {
  const { collisionMapA, collisionMapB } = args;

  const sourceIntoTargetCollisionMap = new Map<EquivalencyKey, Set<string>>();
  // it does not matter which Map we iterate on, the results are the same
  for (const equivalencyKey of collisionMapA.keys()) {
    const collisionsInTarget = collisionMapB.get(equivalencyKey);

    if (collisionsInTarget) {
      sourceIntoTargetCollisionMap.set(equivalencyKey, collisionsInTarget);
    }
  }

  return sourceIntoTargetCollisionMap;
}

/**
 * Builds a collision map for a set of primitives
 * A collision map is an inverted index mapping a primitive key to all primitives that share it
 */
export function getCollisionsWithinPrimitives(args: {
  primitives: WorkflowPrimitive[];
  context?: EquivalencyContext;
}): Map<EquivalencyKey, Set<string>> {
  const { primitives, context } = args;

  const collisionMap = new Map<EquivalencyKey, Set<string>>();
  primitives.forEach(primitive => {
    if (
      !isAction(primitive)
      && !isTag(primitive)
      && !isOutcome(primitive)
      && !isPublishedAttribute(primitive)
      && !isReasonCodeGroup(primitive)
    ) {
      // this warning will go away once all primitives are implemented
      /* eslint-disable-next-line no-console */
      console.warn('Primitive type is not supported', primitive);
      return;
    }

    const equivalencyKey = getEquivalencyKeyForPrimitive({ primitive, context });
    const existingCollisionsForEquivalencyKey = (collisionMap.get(equivalencyKey) ?? new Set());
    const primitiveIdentifier = getPrimitiveIdentifier(primitive);

    existingCollisionsForEquivalencyKey.add(primitiveIdentifier);
    collisionMap.set(equivalencyKey, existingCollisionsForEquivalencyKey);
  });

  return collisionMap;
}

export function buildPrimitives(args: {
  actions?: WorkflowState['workflowData']['actions'];
  tags?: Tag[];
  outcomes?: Outcome[];
  publishedAttributes?: PublishedAttribute[];
  reasonCodeGroups?: ReasonCodeGroup[];
}): WorkflowPrimitive[] {
  const {
    actions, tags, outcomes, publishedAttributes, reasonCodeGroups,
  } = args;

  const primitives: WorkflowPrimitive[] = [
    ...Object.values(actions ?? {}),
    ...(tags ?? []),
    ...(outcomes ?? []),
    ...(publishedAttributes ?? []),
    ...(reasonCodeGroups ?? []),
  ];

  return primitives;
}


function getPrimitiveIdentifier(primitive: WorkflowPrimitive): string {
  if (isTag(primitive)) {
    return primitive.tag_name;
  }

  if (isAction(primitive)) {
    return primitive.publicId;
  }

  if (isOutcome(primitive)) {
    return primitive.outcome;
  }

  if (isPublishedAttribute(primitive)) {
    return primitive.attributeToken;
  }

  if (isReasonCodeGroup(primitive)) {
    return primitive.name;
  }

  /* eslint-disable-next-line no-console */
  console.warn('identifier not supported for primitive yet', primitive);

  return 'UNSUPPORTED_PRIMITIVE';
}


export function appendPrimitiveToCollisionMap(args: {
  collisionMap: Map<EquivalencyKey, Set<string>>;
  equivalencyKey: EquivalencyKey;
  primitive: WorkflowPrimitive;
}): void {
  const { collisionMap, equivalencyKey, primitive } = args;

  const collisionSetForPrimitive = collisionMap.get(equivalencyKey) ?? new Set();

  const primitiveIdentifier = getPrimitiveIdentifier(primitive);
  collisionSetForPrimitive.add(primitiveIdentifier);
  collisionMap.set(equivalencyKey, collisionSetForPrimitive);
}
